import { useMemo } from 'react';
import { useList } from '@refinedev/core';
import { LiveFeedScreen } from '@viq-admin/screens';
import type { LiveFeedResponse } from '@viq-admin/screens';
import { adminFetch } from '../../api/dataProvider';
import { Conference } from '../../api/types';
import { LeadDetailBody } from '../LeadDetail/LeadDetailBody';

/**
 * Customer Live-feed route. Renders the shared LiveFeedScreen (picker + 7s
 * poll + capture cards + lead-detail drawer) wired to the customer plane's
 * `/v1/admin/*` endpoints. Token-scoped — no `?companyId=`, no tenant picker.
 * The fetcher is stable (module-level) since there's no scope to close over.
 */
async function fetchLiveFeed({
  conferenceId,
  since,
  limit,
  signal,
}: {
  conferenceId: string;
  since: string | null;
  limit: number;
  signal: AbortSignal;
}): Promise<LiveFeedResponse> {
  const params = new URLSearchParams();
  if (since) params.set('since', since);
  params.set('limit', String(limit));
  return adminFetch<LiveFeedResponse>(
    `/v1/admin/conferences/${conferenceId}/live-feed?${params.toString()}`,
    { signal },
  );
}

export function LiveFeedRoute() {
  const { result } = useList<Conference>({ resource: 'conferences' });
  const conferences = useMemo(
    () =>
      (result?.data ?? []).map((c) => ({
        id: c.id,
        name: c.name,
        location: c.location ?? null,
        endDate: c.endDate ?? null,
      })),
    [result?.data],
  );

  return (
    <LiveFeedScreen
      conferences={conferences}
      fetchLiveFeed={fetchLiveFeed}
      renderLeadDetail={(id) => <LeadDetailBody id={id} />}
    />
  );
}
